import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Button, Table, Spinner, Form } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { BIEUMAUTIENSI_API } from '../../../../utils/apiUrl';
import { objectShowNumber, role } from '../../../../utils/define';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { createUrlWithParam, checkApiPermission } from '../../../../utils/helper';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import BieuMauTienSiAdd from './BieuMauTienSiAdd';
import BieuMauTienSiEdit from './BieuMauTienSiEdit';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import { userManager } from '../../../../utils/oidc';

const BieuMauTienSi = () => {
    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation();
    const mappings = useSelector((state) => state.apiRoleMapping?.listApiRoleMapping || []);

    const [viewMode, setViewMode] = useState('list');
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    
    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [keyword, setKeyword] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');

    useEffect(() => {
        const paramUrl = new URLSearchParams(location.search.substring(1));
        if (paramUrl.get('page') != null) {
            const parsedPage = parseInt(paramUrl.get('page'), 10);
            if (!isNaN(parsedPage)) setPage(parsedPage);
        }
        if (paramUrl.get('size') != null) {
            const parsedSize = parseInt(paramUrl.get('size'), 10);
            if (!isNaN(parsedSize)) setPageSize(parsedSize);
        }
        if (paramUrl.get('search') != null) {
            setSearchKeyword(paramUrl.get('search'));
            setKeyword(paramUrl.get('search'));
        }

        const fetchRole = async () => {
            const user = await userManager.getUser();
            if (user && user.profile && user.profile.role) {
                const userRoles = Array.isArray(user.profile.role) ? user.profile.role : [user.profile.role];
                setCanAdd(checkApiPermission(mappings, userRoles, 'BieuMauTienSi', 'Create'));
                setCanEdit(checkApiPermission(mappings, userRoles, 'BieuMauTienSi', 'UpdateItem'));
                setCanDelete(checkApiPermission(mappings, userRoles, 'BieuMauTienSi', 'DeleteItem'));
            }
        };
        fetchRole();
    }, [mappings]);

    const replaceHistory = (Page, Size, Search) => {
        history.replace(
            createUrlWithParam(location.pathname, {
                page: Page,
                size: Size,
                search: Search
            })
        );
    };

    // Delete states
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const { isLoading, data: queryData, refetch } = useQuery(
        ['bieuMauTienSiData', page, pageSize, searchKeyword],
        () => {
            const model = {
                Keyword: searchKeyword,
                PageIndex: page,
                PageSize: pageSize
            };
            replaceHistory(page, pageSize, searchKeyword);
            return Get(BIEUMAUTIENSI_API.GET_PAGING, null, model);
        },
        { 
            keepPreviousData: true 
        }
    );

    const items = queryData?.data?.$values ? queryData.data.$values : queryData?.data || [];
    const totalRecords = queryData?.totalRecords || 0;

    useEffect(() => {
        if (items && items.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = items.find(item => item.id === prev.id);
                    return fresh || items[0];
                }
                return items[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [queryData]);

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, searchKeyword);
    };

    const onChangeShowNumber = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setPage(1);
        replaceHistory(1, newSize, searchKeyword);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearchKeyword(keyword);
        replaceHistory(1, pageSize, keyword);
    };

    const deleteFunc = useMutation((id) => DeleteSingle(BIEUMAUTIENSI_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setViewMode('add');
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setViewMode('edit');
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name || 'Bản ghi này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['bieuMauTienSiData']);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    return (
        <div className="container-fluid">
            <Card className="card-sticky-layout card-sticky-layout-lg">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="font-weight-bold m-0">Biểu mẫu tiến sĩ</h4>
                        {canAdd && (
                            <Button variant="success" onClick={handleAdd}>
                                Thêm mới
                            </Button>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="d-flex flex-column p-0">
                    <Card className="card-sticky-layout border-0 shadow-none flex-grow-1">
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex justify-content-between mb-3">
                                <form onSubmit={handleSearch} className="d-flex w-50">
                                    <Form.Control
                                        type="text"
                                        placeholder="Tìm kiếm theo tên biểu mẫu..."
                                        value={keyword}
                                        onChange={(e) => {
                                            setKeyword(e.target.value);
                                            setSearchKeyword(e.target.value);
                                            setPage(1);
                                        }}
                                        className="mr-2"
                                    />
                                    <Button type="submit" variant="primary">
                                        <i className="fas fa-search"></i>
                                    </Button>
                                </form>
                            </div>

                            {isLoading ? (
                                <div className="text-center p-5 m-auto">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive flex-grow-1">
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th className="text-center" width="50">STT</th>
                                                    <th>Tên biểu mẫu</th>
                                                    <th>Mô tả</th>
                                                    <th className="text-center">Đường dẫn</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.length > 0 ? (
                                                    items.map((item, index) => (
                                                        <tr 
                                                            key={item.id}
                                                            onClick={() => setSelectedRow(item)}
                                                            style={{ cursor: 'pointer' }}
                                                            className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                                        >
                                                            <td className="text-center">{(page - 1) * pageSize + index + 1}</td>
                                                            <td>{item.tenBieuMau}</td>
                                                            <td dangerouslySetInnerHTML={{ __html: item.moTa }}></td>
                                                            <td className="text-center">
                                                                {item.duongDanDrive ? (
                                                                    <a 
                                                                        href={item.duongDanDrive.startsWith('http') ? item.duongDanDrive : `http://${item.duongDanDrive}`} 
                                                                        target="_blank" 
                                                                        rel="noreferrer" 
                                                                        className="text-primary"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        style={{ wordBreak: 'break-all', display: 'inline-block', maxWidth: '300px' }}
                                                                    >
                                                                        {item.duongDanDrive}
                                                                    </a>
                                                                ) : (
                                                                    <span className="text-muted">Không có</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center text-muted">
                                                            Chưa có dữ liệu biểu mẫu
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mt-3 p-2">
                                        <div>
                                            <Form.Control as="select" value={pageSize} onChange={onChangeShowNumber} style={{ width: '100px' }}>
                                                {objectShowNumber.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                                                ))}
                                            </Form.Control>
                                        </div>
                                        <div>
                                            <Pagination
                                                itemClass="page-item"
                                                linkClass="page-link"
                                                innerClass="pagination pagination-sm no-margin"
                                                activePage={page}
                                                itemsCountPerPage={pageSize}
                                                totalItemsCount={totalRecords}
                                                pageRangeDisplayed={5}
                                                onChange={changePage}
                                            />
                                        </div>
                                    </div>
                                    
                                    <ConfirmModal
                                        isOpen={openConfirmDeleteModal}
                                        confirmType="delete"
                                        goBackAfterDelete={false}
                                        handleAccept={acceptDelete}
                                        handleCancel={cancelDelete}
                                        content={confirmDelMessage}
                                    />
                                </>
                            )}
                        </Card.Body>
                        {!isLoading && (canEdit || canDelete) && (
                            <Card.Footer className="bg-white card-footer-sticky">
                                <div className="d-flex justify-content-end pr-4">
                                    {canEdit && (
                                        <Button 
                                            variant="info" 
                                            size="sm" 
                                            className="mr-2" 
                                            onClick={() => handleEdit(selectedRow)}
                                            disabled={!selectedRow}
                                        >
                                            <i className="fas fa-edit mr-1"></i> Sửa
                                        </Button>
                                    )}
                                    {canDelete && (
                                        <Button 
                                            variant="danger" 
                                            size="sm" 
                                            onClick={() => deleteAction(selectedRow?.id, selectedRow?.tenBieuMau)}
                                            disabled={!selectedRow}
                                        >
                                            <i className="fas fa-trash mr-1"></i> Xóa
                                        </Button>
                                    )}
                                </div>
                            </Card.Footer>
                        )}
                    </Card>
                </Card.Body>
            </Card>

            {viewMode === 'add' && (
                <BieuMauTienSiAdd
                    setViewMode={setViewMode}
                    refreshList={refetch}
                />
            )}
            {viewMode === 'edit' && dataEdit && (
                <BieuMauTienSiEdit
                    dataEdit={dataEdit}
                    setViewMode={setViewMode}
                    refreshList={refetch}
                />
            )}
        </div>
    );
};

export default BieuMauTienSi;
